import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { AlertCircle } from "lucide-react";
import Footer from './Footer'; 

const LoginForm = ({ onLogin, isLoading, loginError }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [displayError, setDisplayError] = useState(null);

  useEffect(() => {
    if (loginError) {
      setDisplayError(loginError);
    }
  }, [loginError]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data) => {
    setDisplayError(null);
    if (onLogin) {
      await onLogin(data);
    }
  };

  return (
    <div className="w-full bg-white flex flex-col min-h-screen">
      <main className="flex-1 flex justify-center items-center px-4 md:px-8">
        <div className="w-full max-w-7xl">
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 xl:gap-24 items-center"> */}
          <div className="flex justify-center items-center">
            {/* Left Section - Image */}
            {/* <div className="hidden lg:flex justify-end items-center">
              <div className="w-full max-w-sm h-[550px] rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=800&fit=crop"
                  alt="Login"
                  className="w-full h-full object-cover"
                />
              </div>
            </div> */}

            {/* Right Section - Login Form */}
            {/* <div className="w-full max-w-sm flex-shrink-0 mx-auto lg:mx-0"> */}
            <div className="w-full max-w-sm flex-shrink-0 mx-auto">
              <div className="bg-white">
                <div className="mb-8 md:mb-6 text-center">
                  <h1 className="wryven-font text-6xl text-gray-900">Wryven</h1>
                </div>

                {displayError && (
                  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{displayError}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Email address
                    </label>
                    <input
                      type="email"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Please enter a valid email address",
                        },
                      })}
                      placeholder="email address"
                      disabled={isLoading}
                      className={`w-full px-5 py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400 text-base transition-all ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-2">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        {...register("password", {
                          required: "Password is required",
                          minLength: {
                            value: 8,
                            message: "Password must be at least 8 characters",
                          },
                        })}
                        placeholder="Password"
                        disabled={isLoading}
                        className={`w-full px-5 py-3 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400 text-base transition-all ${
                          errors.password ? "border-red-500" : "border-gray-300"
                        } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-700 text-sm font-medium"
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-xs mt-2">
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={!isValid || isLoading}
                    className={`w-full bg-[#b0d284] text-black font-semibold py-3 rounded-lg
    hover:bg-[#9ec170]
    active:scale-[0.98]
    focus:outline-none focus:ring-2 focus:ring-[#b0d284]/60
    transition-all duration-300 text-base
    ${
      !isValid || isLoading
        ? "opacity-70 cursor-not-allowed hover:bg-[#b0d284] active:scale-100"
        : ""
    }
  `}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                        Signing In...
                      </div>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <a
                    href="/forgot-password"
                    size="sm"
                    className="text-sm text-black font-semibold"
                  >
                    Forgot your password?
                  </a>
                </div>

                <div className="my-6 flex items-center">
                  <div className="flex-1 border-t border-gray-200"></div>
                  <span className="px-4 text-gray-500 text-sm">OR</span>
                  <div className="flex-1 border-t border-gray-200"></div>
                </div>

                <p className="text-center text-sm text-black">
                  Don't have an account?{" "}
                  <a
                    href="/signup"
                    className="font-semibold text-[#6b8f3a] hover:text-[#5a7a30] transition-colors"
                  >
                    Sign up
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <Footer />
    </div>
  );
};

export default LoginForm;